import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ThemeContextProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import Layout from './components/Layout/Layout';
import AdminLayout from './components/Layout/AdminLayout';
import ScrollToTop from './components/ScrollToTop';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import DashboardPage from './pages/DashboardPage';
import AdminDashboard from './pages/AdminDashboard';
import ShopsManagementPage from './pages/ShopsManagementPage';
import PaymentManagement from './pages/PaymentManagement';
import ProductsPage from './pages/ProductsPage';
import CustomersPage from './pages/CustomersPage';
import SuppliersPage from './pages/SuppliersPage';
import PurchaseOrdersPage from './pages/PurchaseOrdersPage';
import POSPage from './pages/PosPage';
import SalesManagementPage from './pages/SalesManagementPage';
import ReportsPage from './pages/ReportsPage';
import Settings from './pages/Settings';
import ShopConfigurationPage from './pages/ShopConfigurationPage';
import MyTicketsPage from './pages/MyTicketsPage';
import SupportTicketsPage from './pages/SupportTicketsPage';
import TicketDetailPage from './pages/TicketDetailPage';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import SmartRedirect from './components/SmartRedirect';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? <>{children}</> : <Navigate to="/login" replace />;
};

// Routes Content Component - uses auth context
const RoutesContent: React.FC = () => {
  const { user } = useAuth();

  // Redirect based on user role
  const getDefaultRoute = () => {
    if (!user) return '/login';
    return user.role === 'super_admin' ? '/admin' : '/smart-redirect';
  };

  return (
    <Routes>
      {/* Landing Page - Public Home */}
      <Route path="/" element={<LandingPage />} />

      <Route
        path="/login"
        element={user ? <Navigate to={getDefaultRoute()} replace /> : <Login />}
      />

      <Route
        path="/forgot-password"
        element={user ? <Navigate to={getDefaultRoute()} replace /> : <ForgotPassword />}
      />

      {/* Smart Redirect Route - redirects to first accessible module */}
      <Route
        path="/smart-redirect"
        element={
          <ProtectedRoute>
            <SmartRedirect />
          </ProtectedRoute>
        }
      />

      {/* Super Admin Routes */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute>
            {user?.role === 'super_admin' ? (
              <AdminLayout>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <AdminProtectedRoute requiredModule="dashboard">
                        <AdminDashboard />
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/shops"
                    element={
                      <AdminProtectedRoute requiredModule="shops_management">
                        <ShopsManagementPage />
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/payments"
                    element={
                      <AdminProtectedRoute requiredModule="payment_management">
                        <PaymentManagement />
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/statistics"
                    element={
                      <AdminProtectedRoute requiredModule="dashboard">
                        <AdminDashboard />
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/support-tickets"
                    element={
                      <AdminProtectedRoute requiredModule="support_tickets">
                        <SupportTicketsPage />
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/support-tickets/:id"
                    element={
                      <AdminProtectedRoute requiredModule="support_tickets">
                        <TicketDetailPage />
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <AdminProtectedRoute requiredModule="settings">
                        <Settings />
                      </AdminProtectedRoute>
                    }
                  />
                  <Route path="*" element={<Navigate to="/admin" replace />} />
                </Routes>
              </AdminLayout>
            ) : (
              <Navigate to="/dashboard" replace />
            )}
          </ProtectedRoute>
        }
      />

      {/* Shop Owner/Staff Routes */}
      <Route
        path="/dashboard/*"
        element={
          <ProtectedRoute>
            {user?.role !== 'super_admin' ? (
              <Layout>
                <Routes>
                  <Route path="/" element={<SmartRedirect />} />
                  <Route path="/overview" element={<DashboardPage />} />
                  <Route path="/products" element={<ProductsPage />} />
                  <Route path="/customers" element={<CustomersPage />} />
                  <Route path="/pos" element={<POSPage />} />
                  <Route path="/sales" element={<SalesManagementPage />} />
                  <Route path="/suppliers" element={<SuppliersPage />} />
                  <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/configuration" element={<ShopConfigurationPage />} />
                  <Route path="/my-tickets" element={<MyTicketsPage />} />
                  <Route path="/my-tickets/:id" element={<TicketDetailPage />} />
                  <Route path="*" element={<SmartRedirect />} />
                </Routes>
              </Layout>
            ) : (
              <Navigate to="/admin" replace />
            )}
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to home */}
      <Route
        path="*"
        element={<Navigate to="/" replace />}
      />
    </Routes>
  );
};

// App Routes Wrapper - provides context boundary
const AppRoutes: React.FC = () => {
  return <RoutesContent />;
};

// Main App Component
const App: React.FC = () => {
  return (
    <ThemeContextProvider>
      <AuthProvider>
        <SettingsProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
            <ScrollToTop />
            <div className="App">
              <AppRoutes />
              <Toaster
                position="bottom-right"
                containerStyle={{
                  bottom: '80px', // Space above footer (~32px footer + 48px margin)
                  right: '20px',
                  zIndex: 99999,
                }}
                toastOptions={{
                  style: {
                    background: 'transparent',
                    border: 'none',
                    boxShadow: 'none',
                    padding: 0,
                  },
                  duration: Infinity, // We control duration in customToast
                }}
              />
            </div>
          </Router>
        </SettingsProvider>
      </AuthProvider>
    </ThemeContextProvider>
  );
};

export default App;